
import { useState } from 'react';

export default function AlternativesComparison() {
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [selectedAlternative, setSelectedAlternative] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const alternatives = [
    {
      id: 'electric-balancer',
      name: '電動バランサ',
      category: '人力補助系',
      description: '重量物を無重力状態にして人力での搬送を支援する装置。精密な位置決めが可能で、熟練作業者の技術を活かせます。',
      image: 'https://readdy.ai/api/search-image?query=Electric%20balancer%20system%20in%20factory%2C%20lifting%20heavy%20packages%20with%20precision%20control%2C%20worker%20operating%20balanced%20load%20handling%20equipment%2C%20industrial%20workspace%20with%20safety%20features&width=600&height=400&seq=electric-balancer&orientation=landscape',
      pros: [
        '初期投資が比較的低い',
        '精密な位置決めが可能',
        '既存レイアウト変更不要',
        '多品種対応が容易'
      ],
      cons: [
        '人員は必要',
        '連続稼働には限界',
        '作業者のスキルに依存',
        '処理能力に上限'
      ],
      cost: '300-800万円',
      roi: '1.5-2年',
      suitability: '多品種少量生産',
      specifications: {
        '可搬重量': '5-100kg',
        '作業半径': '2-4m',
        '昇降ストローク': '1-3m',
        '位置決め精度': '±1mm',
        '電源': 'AC200V',
        '設置面積': '4-8㎡'
      }
    },
    {
      id: 'assist-suit',
      name: 'アシストスーツ',
      category: '装着型補助',
      description: '作業者が装着して腰部負担を軽減する外骨格システム。重量物の持ち上げ作業での腰痛予防に効果的です。',
      image: 'https://readdy.ai/api/search-image?query=Worker%20wearing%20exoskeleton%20assist%20suit%20lifting%20heavy%20boxes%20in%20warehouse%2C%20ergonomic%20support%20equipment%2C%20back%20support%20technology%2C%20industrial%20safety%20gear%20in%20action&width=600&height=400&seq=assist-suit&orientation=landscape',
      pros: [
        '最も低コスト',
        '即座に導入可能',
        '場所を選ばない',
        '腰痛予防効果大'
      ],
      cons: [
        '根本的自動化ではない',
        '装着の手間',
        'バッテリー管理必要',
        '完全な負荷軽減は困難'
      ],
      cost: '20-50万円/台',
      roi: '0.5-1年',
      suitability: '緊急的な負荷軽減',
      specifications: {
        '補助力': '15-25kgf',
        '連続使用時間': '6-8時間',
        '充電時間': '2-4時間',
        '重量': '3-6kg',
        '適用体重': '50-100kg',
        '動作温度': '-10〜40℃'
      }
    },
    {
      id: 'pallet-leveler',
      name: 'パレットレベラー',
      category: '作業環境改善',
      description: 'パレット高さを一定に保ち、作業者の腰部負担を軽減。積み付け作業の姿勢改善により作業効率向上を図ります。',
      image: 'https://readdy.ai/api/search-image?query=Pallet%20leveler%20system%20maintaining%20constant%20working%20height%2C%20automated%20height%20adjustment%20platform%2C%20ergonomic%20loading%20dock%20equipment%2C%20worker-friendly%20warehouse%20solution&width=600&height=400&seq=pallet-leveler&orientation=landscape',
      pros: [
        '腰痛対策に効果的',
        '作業効率向上',
        '既存システムに追加可能',
        'メンテナンス簡単'
      ],
      cons: [
        '根本的省人化にならない',
        '設置場所が限定的',
        '重量制限あり',
        '多品種対応困難'
      ],
      cost: '150-400万円',
      roi: '2-3年',
      suitability: '単一品種大量生産',
      specifications: {
        '最大積載重量': '1-3t',
        '昇降範囲': '300-1500mm',
        '昇降速度': '5-15mm/s',
        'テーブルサイズ': '1200×1000mm',
        '電源': 'AC200V 3相',
        '騒音レベル': '65dB以下'
      }
    },
    {
      id: 'power-assist',
      name: 'パワーアシスト台車',
      category: '搬送補助系',
      description: '電動アシスト機能付き台車で重量物の搬送負荷を軽減。坂道や長距離搬送での威力を発揮します。',
      image: 'https://readdy.ai/api/search-image?query=Power-assisted%20cart%20system%20moving%20heavy%20loads%2C%20electric%20motorized%20trolley%20in%20industrial%20setting%2C%20automated%20material%20handling%20cart%2C%20efficient%20warehouse%20transportation&width=600&height=400&seq=power-assist&orientation=landscape',
      pros: [
        '搬送負荷大幅軽減',
        '長距離搬送対応',
        '坂道でも楽々移動',
        '停電時も手動使用可能'
      ],
      cons: [
        '積み付け作業は手作業',
        '充電管理必要',
        '通路幅の制約',
        '段差対応に限界'
      ],
      cost: '80-200万円',
      roi: '1-2年',
      suitability: '搬送距離が長い現場',
      specifications: {
        '最大積載重量': '300-1000kg',
        'アシスト能力': '最大傾斜15°',
        '連続走行距離': '20-50km',
        '最高速度': '6km/h',
        '充電時間': '6-8時間',
        '車輪径': '200-300mm'
      }
    },
    {
      id: 'collaborative-robot',
      name: '協働ロボット',
      category: '部分自動化',
      description: '人と同じ空間で安全に作業する小型ロボット。パレタイザーより小規模ながら、柔軟性の高い自動化を実現します。',
      image: 'https://readdy.ai/api/search-image?query=Collaborative%20robot%20working%20alongside%20human%20worker%2C%20safe%20industrial%20cobot%20handling%20packages%2C%20human-robot%20cooperation%20in%20warehouse%2C%20flexible%20automation%20solution&width=600&height=400&seq=collaborative-robot&orientation=landscape',
      pros: [
        '人との協働が安全',
        '設置が比較的簡単',
        'プログラム変更容易',
        '段階的自動化可能'
      ],
      cons: [
        '処理能力は限定的',
        '重量物対応困難',
        '安全柵設置推奨',
        'ティーチング必要'
      ],
      cost: '500-1500万円',
      roi: '2-3年',
      suitability: '中量物・多品種対応',
      specifications: {
        '可搬重量': '3-35kg',
        '作業半径': '500-1700mm',
        '繰り返し精度': '±0.1mm',
        '最大速度': '2-3m/s',
        '安全機能': '力覚センサー内蔵',
        '消費電力': '200-500W'
      }
    }
  ];

  const comparisonData = [
    {
      criteria: '初期投資',
      palletizer: '2000-4000万円',
      alternatives: {
        'electric-balancer': '300-800万円',
        'assist-suit': '20-50万円',
        'pallet-leveler': '150-400万円',
        'power-assist': '80-200万円',
        'collaborative-robot': '500-1500万円'
      }
    },
    {
      criteria: '省人化効果',
      palletizer: '90-100%',
      alternatives: {
        'electric-balancer': '30-50%',
        'assist-suit': '10-20%',
        'pallet-leveler': '20-30%',
        'power-assist': '25-40%',
        'collaborative-robot': '60-80%'
      }
    },
    {
      criteria: '処理能力',
      palletizer: '600-1200/時',
      alternatives: {
        'electric-balancer': '150-300/時',
        'assist-suit': '100-200/時',
        'pallet-leveler': '200-400/時',
        'power-assist': '180-350/時',
        'collaborative-robot': '200-400/時'
      }
    },
    {
      criteria: '設置面積',
      palletizer: '80-150㎡',
      alternatives: {
        'electric-balancer': '4-8㎡',
        'assist-suit': '0㎡',
        'pallet-leveler': '2-4㎡',
        'power-assist': '1-2㎡',
        'collaborative-robot': '6-12㎡'
      }
    },
    {
      criteria: '導入期間',
      palletizer: '6-12ヶ月',
      alternatives: {
        'electric-balancer': '1-2ヶ月',
        'assist-suit': '即日',
        'pallet-leveler': '1ヶ月',
        'power-assist': '2週間',
        'collaborative-robot': '2-4ヶ月'
      }
    },
    {
      criteria: '投資回収',
      palletizer: '3-5年',
      alternatives: {
        'electric-balancer': '1.5-2年',
        'assist-suit': '0.5-1年',
        'pallet-leveler': '2-3年',
        'power-assist': '1-2年',
        'collaborative-robot': '2-3年'
      }
    }
  ];

  const handleInquirySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d3c8ocsef3sotc9j5t90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        alert('お問い合わせありがとうございます。担当者より3営業日以内にご連絡いたします。');
        form.reset();
        setShowInquiryForm(false);
      }
    } catch (error) {
      alert('送信に失敗しました。お電話でのお問い合わせもお受けしております。');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-800 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <a href="/" className="text-2xl font-bold hover:text-orange-400 cursor-pointer transition-colors" style={{ fontFamily: '"Arial", "Helvetica", sans-serif' }}>パレタイザーナビ</a>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <a href="/palletizer-basics" className="hover:text-orange-400 transition-colors cursor-pointer">パレタイザーとは</a>
            <a href="/selection-guide" className="hover:text-orange-400 transition-colors cursor-pointer">機種選定ガイド</a>
            <a href="/decision-criteria" className="hover:text-orange-400 transition-colors cursor-pointer">導入判断基準</a>
            <a href="/case-studies" className="hover:text-orange-400 transition-colors cursor-pointer">導入事例</a>
            <a href="/manufacturers-products" className="hover:text-orange-400 transition-colors cursor-pointer">メーカー・製品</a>
            <a href="/peripheral-systems" className="hover:text-orange-400 transition-colors cursor-pointer">周辺機器</a>
            <a href="/alternatives-comparison" className="hover:text-orange-400 transition-colors cursor-pointer">代替案・比較</a>
          </nav>

          <div className="flex items-center space-x-6">
            <button 
              onClick={() => setShowInquiryForm(true)}
              className="bg-orange-500 hover:bg-orange-600 px-6 py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              見積依頼・お問い合わせ
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white cursor-pointer"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-700">
            <nav className="flex flex-col space-y-4 mt-4">
              <a href="/palletizer-basics" className="hover:text-orange-400 transition-colors cursor-pointer">パレタイザーとは</a>
              <a href="/selection-guide" className="hover:text-orange-400 transition-colors cursor-pointer">機種選定ガイド</a>
              <a href="/decision-criteria" className="hover:text-orange-400 transition-colors cursor-pointer">導入判断基準</a>
              <a href="/case-studies" className="hover:text-orange-400 transition-colors cursor-pointer">導入事例</a>
              <a href="/manufacturers-products" className="hover:text-orange-400 transition-colors cursor-pointer">メーカー・製品</a>
              <a href="/peripheral-systems" className="hover:text-orange-400 transition-colors cursor-pointer">周辺機器</a>
              <a href="/alternatives-comparison" className="hover:text-orange-400 transition-colors cursor-pointer">代替案・比較</a>
              <button 
                onClick={() => {
                  setShowInquiryForm(true);
                  setMobileMenuOpen(false);
                }}
                className="bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors text-left"
              >
                見積依頼・お問い合わせ
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center space-x-2 text-sm text-slate-600">
            <a href="/" className="hover:text-blue-600 cursor-pointer">ホーム</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-slate-800 font-semibold">代替案・比較</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6">パレタイザーの代替案</h1>
          <p className="text-xl text-indigo-100 mb-8">段階的自動化で最適解を見つける</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-6">段階的アプローチの重要性</h2>
              <p className="text-lg text-slate-600 mb-6">
                パレタイザーは効果的な自動化ソリューションですが、初期投資や設置条件により導入が困難な場合があります。
                そのような場合に、段階的に作業負荷を軽減し、将来の完全自動化への基盤を築く代替案をご提案いたします。
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-indigo-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">段階的投資</h3>
                    <p className="text-slate-600">小さな投資から始めて効果を確認</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-indigo-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">即効性</h3>
                    <p className="text-slate-600">導入後すぐに効果を実感</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-indigo-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">リスク分散</h3>
                    <p className="text-slate-600">大規模投資前のリスク軽減</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Phased%20automation%20approach%20diagram%20showing%20gradual%20implementation%20from%20manual%20to%20fully%20automated%20palletizing%2C%20step-by-step%20industrial%20improvement%20process&width=600&height=400&seq=phased-approach&orientation=landscape"
                alt="段階的自動化アプローチ"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Alternative Solutions */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">5つの代替ソリューション</h2>
          <div className="space-y-12">
            {alternatives.map((alternative, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-8">
                  <div>
                    <img 
                      src={alternative.image}
                      alt={alternative.name}
                      className="w-full h-80 object-cover"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center mb-4">
                      <h3 className="text-3xl font-bold text-slate-800 mr-4">{alternative.name}</h3>
                      <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {alternative.category}
                      </span>
                    </div>
                    <p className="text-lg text-slate-600 mb-6">{alternative.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="font-bold text-green-600 mb-3">メリット</h4>
                        <ul className="space-y-2">
                          {alternative.pros.map((pro, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                              <span className="text-sm text-slate-600">{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-red-600 mb-3">制約事項</h4>
                        <ul className="space-y-2">
                          {alternative.cons.map((con, idx) => (
                            <li key={idx} className="flex items-start">
                              <i className="ri-close-line text-red-600 mr-2 mt-1"></i>
                              <span className="text-sm text-slate-600">{con}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-slate-50 p-4 rounded-lg text-center">
                        <h5 className="font-semibold text-slate-800 mb-2">初期投資</h5>
                        <p className="text-lg font-bold text-indigo-600">{alternative.cost}</p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg text-center">
                        <h5 className="font-semibold text-slate-800 mb-2">投資回収</h5>
                        <p className="text-lg font-bold text-indigo-600">{alternative.roi}</p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg text-center">
                        <h5 className="font-semibold text-slate-800 mb-2">適用場面</h5>
                        <p className="text-sm text-slate-600">{alternative.suitability}</p>
                      </div>
                    </div>

                    <button 
                      onClick={() => setSelectedAlternative(selectedAlternative === alternative.id ? null : alternative.id)}
                      className="bg-indigo-500 hover:bg-indigo-600 px-6 py-2 rounded-lg text-white font-bold whitespace-nowrap cursor-pointer transition-colors"
                    >
                      {selectedAlternative === alternative.id ? '詳細を閉じる' : '仕様詳細を見る'}
                    </button>

                    {selectedAlternative === alternative.id && (
                      <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
                        <h5 className="font-bold text-slate-800 mb-3">詳細仕様</h5>
                        <div className="grid md:grid-cols-2 gap-4">
                          {Object.entries(alternative.specifications).map(([key, value], specIndex) => (
                            <div key={specIndex} className="flex justify-between">
                              <span className="font-semibold text-slate-700">{key}:</span>
                              <span className="text-slate-600">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">パレタイザーとの比較表</h2>
          
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full bg-white shadow-lg rounded-lg overflow-hidden">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="p-4 text-left">比較項目</th>
                  <th className="p-4 text-center">パレタイザー</th>
                  <th className="p-4 text-center">電動バランサ</th>
                  <th className="p-4 text-center">アシストスーツ</th>
                  <th className="p-4 text-center">パレットレベラー</th>
                  <th className="p-4 text-center">パワーアシスト</th>
                  <th className="p-4 text-center">協働ロボット</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-4 font-semibold bg-slate-50">{row.criteria}</td>
                    <td className="p-4 text-center font-bold text-blue-600">{row.palletizer}</td>
                    {alternatives.map((alt, altIndex) => (
                      <td key={altIndex} className="p-4 text-center">{row.alternatives[alt.id as keyof typeof row.alternatives]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-blue-800 mb-4">パレタイザー（基準）</h3>
              <div className="space-y-2">
                {comparisonData.map((row, index) => (
                  <div key={index} className="flex justify-between">
                    <span className="font-semibold">{row.criteria}:</span>
                    <span className="font-bold text-blue-600">{row.palletizer}</span>
                  </div>
                ))}
              </div>
            </div>
            {alternatives.map((alternative, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-slate-800 mb-4">{alternative.name}</h3>
                <div className="space-y-2">
                  {comparisonData.map((row, rowIndex) => (
                    <div key={rowIndex} className="flex justify-between">
                      <span className="font-semibold">{row.criteria}:</span>
                      <span>{row.alternatives[alternative.id as keyof typeof row.alternatives]}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Decision Matrix */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-16">最適解の選び方</h2>
          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                scenario: '初期投資を抑えたい',
                priority: '低コスト',
                recommendations: [
                  { solution: 'アシストスーツ', score: '★★★★★', reason: '最も低コストで即効性あり' },
                  { solution: 'パレットレベラー', score: '★★★★☆', reason: '腰痛対策に効果的' },
                  { solution: '電動バランサ', score: '★★★☆☆', reason: '中程度の投資で効果実感' }
                ]
              },
              {
                scenario: '部分的な自動化から始めたい',
                priority: '段階的導入',
                recommendations: [
                  { solution: '協働ロボット', score: '★★★★★', reason: '人と協働で安全性高い' },
                  { solution: 'パワーアシスト', score: '★★★★☆', reason: '重労働解消と効率化両立' },
                  { solution: '電動バランサ', score: '★★★☆☆', reason: '精密作業に対応' }
                ]
              },
              {
                scenario: '労働環境改善を優先したい',
                priority: '安全・健康',
                recommendations: [
                  { solution: 'パレットレベラー', score: '★★★★★', reason: '腰痛予防効果抜群' },
                  { solution: 'アシストスーツ', score: '★★★★☆', reason: '身体負担軽減' },
                  { solution: 'パワーアシスト', score: '★★★☆☆', reason: '重量物取扱い安全化' }
                ]
              }
            ].map((scenario, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-slate-800 mb-4">{scenario.scenario}</h3>
                <div className="bg-indigo-50 p-3 rounded-lg mb-6">
                  <span className="text-sm font-semibold text-indigo-800">重視点: </span>
                  <span className="text-sm text-indigo-600">{scenario.priority}</span>
                </div>
                <div className="space-y-4">
                  {scenario.recommendations.map((rec, recIndex) => (
                    <div key={recIndex} className="border border-slate-200 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-bold text-slate-800">{rec.solution}</h4>
                        <span className="text-yellow-500">{rec.score}</span>
                      </div>
                      <p className="text-sm text-slate-600">{rec.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-8">最適な解決策を一緒に見つけませんか？</h2>
          <p className="text-xl mb-8">現場の状況に応じて、最適なソリューションをご提案いたします</p>
          <div className="flex justify-center space-x-6">
            <a 
              href="/decision-criteria"
              className="bg-white text-indigo-600 hover:bg-slate-100 px-10 py-4 rounded-lg text-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              導入判断基準を確認
            </a>
            <button 
              onClick={() => setShowInquiryForm(true)}
              className="bg-orange-500 hover:bg-orange-600 px-10 py-4 rounded-lg text-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              最適解を相談する
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Arial", "Helvetica", sans-serif' }}>パレタイザーナビ</h3>
              <p className="text-slate-300 mb-4">パレタイザー専門企業として、製造業・物流業の自動化をサポートします。</p>
              <div className="flex space-x-4">
                <i className="ri-phone-line text-orange-400"></i>
                <span>03-1234-5678</span>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">サービス</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">機械式パレタイザー</a></li>
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">多関節ロボット</a></li>
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">直交ロボット</a></li>
                <li><a href="/peripheral-systems" className="hover:text-orange-400 cursor-pointer">保守・メンテナンス</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">詳細情報</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="/palletizer-basics" className="hover:text-orange-400 cursor-pointer">パレタイザーとは</a></li>
                <li><a href="/decision-criteria" className="hover:text-orange-400 cursor-pointer">導入判断基準</a></li>
                <li><a href="/case-studies" className="hover:text-orange-400 cursor-pointer">導入事例</a></li>
                <li><a href="/manufacturers-products" className="hover:text-orange-400 cursor-pointer">メーカー・製品</a></li>
                <li><a href="/peripheral-systems" className="hover:text-orange-400 cursor-pointer">周辺機器・システム</a></li>
                <li><a href="/alternatives-comparison" className="hover:text-orange-400 cursor-pointer">代替案・比較</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">会社情報</h4>
              <ul className="space-y-2 text-slate-300">
                <li>〒174-0000 東京都板橋区</li>
                <li>設立：1985年</li>
                <li>従業員数：120名</li>
                <li>ISO9001認証取得</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2024 パレタイザーナビ. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-orange-400 hover:text-orange-300 ml-2">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Inquiry Form Modal */}
      {showInquiryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-slate-800">最適解のご相談</h3>
                <button 
                  onClick={() => setShowInquiryForm(false)}
                  className="text-slate-500 hover:text-slate-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form id="alternatives-comparison-form" data-readdy-form onSubmit={handleInquirySubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">会社名 *</label>
                    <input 
                      type="text" 
                      name="company" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">お名前 *</label>
                    <input 
                      type="text" 
                      name="name" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">電話番号 *</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">メールアドレス *</label>
                    <input 
                      type="email" 
                      name="email" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">最も重視する点</label>
                  <select name="priority" className="w-full p-3 pr-8 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">選択してください</option>
                    <option value="低コスト">初期投資を抑えたい</option>
                    <option value="段階的導入">部分的な自動化から始めたい</option>
                    <option value="安全・健康">労働環境改善を優先したい</option>
                    <option value="効率化">処理能力向上を重視したい</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">興味のある代替案</label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {alternatives.map((alt, index) => (
                      <label key={index} className="flex items-center">
                        <input type="checkbox" name="interested_alternatives" value={alt.name} className="mr-2" />
                        {alt.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">現在の課題・ご相談内容</label>
                  <textarea 
                    name="consultation_details" 
                    rows={4} 
                    maxLength={500}
                    className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="現在の作業状況や改善したい点をお聞かせください（500文字以内）"
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
                >
                  送信する
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
